import React from 'react';
import { useTranslation } from 'react-i18next';
import { HomeTaskAttachments } from '@/components/HomeTaskModal/HomeTaskAttachments';
import { Calendar } from '@/components/Icons/Calendar';
import { Pencil } from '@/components/Icons/Pencil';
import type { Attachment } from '@/types/attachment';
import type { HomeTaskDetails } from '@/types/diary';
import { formatDayMonthWeekday, formatShortDateTime } from '@/utils/date';
import { sanitizeRichTextHtml } from '@/utils/html';
import { joinNonEmpty } from '@/utils/text';

interface Props {
  homeTask: HomeTaskDetails;
  onPreview: (attachment: Attachment) => void;
}

export const HomeTaskDetailsCard: React.FC<Props> = ({
  homeTask,
  onPreview,
}) => {
  const { t } = useTranslation();

  const {
    journalName,
    lessonDate,
    authorName,
    timeCreated,
    lastModification,
    task,
  } = homeTask;
  const deadline = t('diary.homeTask.deadline', {
    date: formatDayMonthWeekday(lessonDate),
  });
  const added = t('diary.homeTask.added', {
    value: joinNonEmpty([authorName, formatShortDateTime(timeCreated)]),
  });
  const edited = t('diary.homeTask.edited', {
    value: joinNonEmpty([
      lastModification.authorName,
      formatShortDateTime(lastModification.timeModified),
    ]),
  });
  const isEdited =
    new Date(lastModification.timeModified) > new Date(timeCreated);

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-card p-4 shadow-card contrast:border contrast:border-border">
      <p className="text-callout-16 truncate text-foreground">{journalName}</p>

      <div className="flex flex-col gap-2 border-b border-divider pb-4">
        <div className="flex items-start gap-2">
          <Calendar className="mt-0.5 size-4 shrink-0 text-subtle-foreground" />
          <p className="text-body-14 min-w-0 flex-1 text-subtle-foreground">
            {deadline}
          </p>
        </div>

        <div className="flex items-start gap-2">
          <Pencil className="mt-0.5 size-4 shrink-0 text-subtle-foreground" />
          <div className="text-body-14 flex min-w-0 flex-1 flex-col text-subtle-foreground">
            <p>{added}</p>
            {isEdited && <p>{edited}</p>}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-subhead-14 text-foreground">
          {t('diary.homeTask.assigned')}
        </p>

        {!task.text && (
          <p className="text-body-14 text-muted-foreground">
            {t('diary.emptyDescription')}
          </p>
        )}
        {!!task.text && (
          <div
            className="rich-text text-body-14 text-muted-foreground"
            dangerouslySetInnerHTML={{
              __html: sanitizeRichTextHtml(task.text),
            }}
          />
        )}
      </div>

      <HomeTaskAttachments
        attachments={task.attachments}
        onPreview={onPreview}
      />
    </div>
  );
};
